<script setup lang="ts">
interface Props {
  listType?: "store" | "menu";
  product: Record<string, unknown>;
  cartItem?: boolean | Record<string, unknown>;
  quantity?: number;
  cartLoading?: boolean;
  disableOperators?: boolean;
  makerName?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  listType: "store",
  cartItem: false,
  quantity: 0,
  cartLoading: false,
  disableOperators: false,
  makerName: null,
});

const emit = defineEmits<{
  returnProduct: [payload: { quantity: number; product: Record<string, unknown> }];
  updateFromPage: [payload: Record<string, unknown>];
}>();

const { attachmentUrl, staticUrl } = useImageUrl();

const hasImage = computed(() => {
  const attachments = props.product.attachments as Record<string, unknown> | undefined;
  const mainImage = attachments?.mainImage as unknown[] | undefined;
  return mainImage && mainImage.length > 0;
});

const outOfStock = computed(() => {
  return (
    (props.product.stock_count as number) > 0 && (props.product.stock_remaining as number) === 0
  );
});

function mainImageUrl(): string {
  const attachments = props.product.attachments as Record<string, unknown>;
  const mainImage = attachments?.mainImage as { file_path_small?: string }[];
  if (mainImage?.[0]?.file_path_small) {
    return attachmentUrl(mainImage, "small");
  }
  return staticUrl("/Images/site-images/placeholder.png");
}

function returnClickEvent() {
  emit("returnProduct", {
    quantity: (props.product.min_order_amount as number) || 1,
    product: props.product,
  });
}

function changeQuantity(payload: Record<string, unknown>) {
  if (props.product) {
    emit("updateFromPage", payload);
  }
}

function isHot(product: Record<string, unknown>): boolean {
  return !!product?.is_hot;
}
</script>

<template>
  <div class="product-list">
    <div
      class="product-list__list"
      :class="[cartItem && 'product-added', outOfStock && 'opacity']"
    >
      <div
        v-if="outOfStock"
        class="product-list__list--unavailable button text-shadow"
      >
        Out of stock
      </div>
      <div
        class="product-list__list--container"
        @click="!outOfStock && !disableOperators && returnClickEvent()"
      >
        <div class="content">
          <div
            v-if="hasImage"
            :style="`background-image: url('${mainImageUrl()}')`"
            class="content__image"
          ></div>
          <div
            class="content__details"
            :style="`width: ${hasImage ? 'calc(95% - 7rem)' : '100%'}; padding: ${hasImage ? '0 1rem' : '0 1rem 0 0'};`"
          >
            <div class="content__details--title-wrapper">
              <p
                class="content__details--title grey-text"
                v-html="product.name"
              ></p>
              <Badge
                class="temperature-badge"
                :class="isHot(product) ? 'temperature-badge--hot' : 'temperature-badge--cold'"
                variant="default"
                size="sm"
              >
                {{ isHot(product) ? "Hot" : "Cold" }}
              </Badge>
            </div>
            <p
              class="content__details--description small-text light-grey-text"
              v-html="((product.description as string) || '').replace(/<\/?[^>]+(>|$)/g, '')"
            ></p>
            <div class="content__details--price">
              <p class="price grey-text heavy">
                £{{ (((product.price_gross as number) || 0) / 100).toFixed(2) }}
              </p>
            </div>
            <!-- A catalogue card only ever trips the bundle half of case A. -->
            <AllergenDeclaration
              class="content__details--declaration grey-text"
              :line="product"
              :maker-name="makerName"
              :tooltip="true"
            />
          </div>
        </div>
        <div class="servings">
          <Badge
            v-if="(product.min_order_amount as number) > 1"
            variant="default"
            size="sm"
          >
            Min {{ product.min_order_amount }}
          </Badge>
          <Badge
            v-if="(product.servings as number) > 1"
            variant="default"
            size="sm"
          >
            Serves {{ product.servings }}
          </Badge>
          <Badge
            v-if="(product.pieces as number) > 1"
            variant="default"
            size="sm"
          >
            {{ product.pieces }} pieces
          </Badge>
          <Badge
            v-if="product.individually_packaged"
            variant="default"
            size="sm"
          >
            Individually packaged
          </Badge>
        </div>
      </div>
      <div class="quantities">
        <QuantityOperator
          v-if="listType === 'store'"
          :disabled="disableOperators || outOfStock"
          :disable-minus="
            Array.isArray(product.modifierGroups) &&
            (product.modifierGroups as unknown[]).length > 0
          "
          :product-quantity="quantity"
          :item="product"
          :modifiers="(product.appliedModifiers as unknown[]) || []"
          :min-quantity="(product.min_order_amount as number) || 1"
          :picker="true"
          :vertical="true"
          @return-quantity="
            !outOfStock &&
            Array.isArray(product.modifierGroups) &&
            (product.modifierGroups as unknown[]).length === 0
              ? changeQuantity($event)
              : returnClickEvent()
          "
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.product-list {
  position: relative;
  margin: 1rem 1% 0 0;
  width: 49%;
  display: flex;
  z-index: 1;
  @media (max-width: $laptop) {
    display: block;
    width: 100%;
  }

  &:hover {
    z-index: 2;
  }

  &__list {
    cursor: pointer;
    border: 1px solid var(--color-grey-3);
    background-color: var(--color-white-1);
    border-radius: var(--radius);
    box-shadow: var(--shadow-1);
    transition:
      box-shadow var(--dur-base) var(--ease-out),
      transform var(--dur-base) var(--ease-out);
    display: flex;
    align-items: stretch;
    width: 90%;
    padding: var(--space-3);
    @media (max-width: $laptop) {
      width: 93%;
    }
    // No pink-border hover — punchier shadow + lift only, matching main's treatment.
    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      transform: translateY(-2px);
    }

    &--unavailable {
      background-color: var(--color-grey-1);
      color: var(--color-white-1);
      margin: var(--space-2);
      top: 0;
      left: 0;
      z-index: 2222;
      border-radius: var(--radius-pill);
      padding: var(--space-1) var(--space-2);
      font-size: var(--text-xs) !important;
      font-weight: var(--weight-semibold);
      letter-spacing: 0.04em;
      text-transform: uppercase;
      position: absolute;
    }

    &--container {
      align-items: center;
      width: 93%;
      min-width: 0;

      .content {
        display: flex;
        align-items: flex-start;
        justify-content: flex-start;
        gap: var(--space-3);

        &__image {
          height: 88px;
          width: 88px;
          min-width: 88px;
          background-position: center;
          background-size: cover;
          border-radius: var(--radius-sm);
          overflow: hidden;
          flex-shrink: 0;
          img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            border-radius: var(--radius-sm);
          }
        }

        &__details {
          flex: 1;
          min-width: 0;

          &--title {
            font-size: var(--text-base);
            font-weight: var(--weight-semibold);
            color: var(--color-grey-1);
            white-space: pre-wrap;
            word-wrap: break-word;
            overflow-wrap: break-word;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
            font-family: inherit;
            margin: 0;
          }

          &--description {
            margin: var(--space-1) 0 0;
            font-size: var(--text-sm);
            line-height: 1.5;
            color: var(--color-grey-2);
            white-space: pre-wrap;
            word-wrap: break-word;
            overflow-wrap: break-word;
            font-family: inherit;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            p {
              margin: 0;
              padding: 0;
            }
          }

          &--declaration {
            margin: var(--space-1) 0 0;
          }

          &--price {
            margin-top: var(--space-2);
            .price_gross {
              margin: 0;
              font-weight: var(--weight-semibold);
              color: var(--color-grey-1);
            }
          }
        }
      }

      .servings {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-2);
        margin-top: var(--space-3);
        width: 100%;
      }
    }

    .quantities {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      margin-left: var(--space-2);
    }
  }
}

.product-added {
  border-left: 2px solid var(--color-pink-1) !important;
}

// Hot/Cold metadata — filled pastels matching the dietary badge family.
// Cold sits in soft-sky blue, Hot in muted neutral so warm dishes don't
// fight the price/CTA for attention.
.temperature-badge {
  flex-shrink: 0;
  &--cold {
    background-color: var(--color-sky-2) !important;
    border-color: transparent !important;
    color: var(--color-sky-1) !important;
    .badge__label {
      color: var(--color-sky-1) !important;
    }
  }
}

.content__details--title-wrapper {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 0.5rem;
}
</style>
